import { useParams } from 'react-router-dom';
import { useMemo } from 'react';
import { Box, Typography, CircularProgress, Alert, Paper } from '@mui/material';
import { Breadcrumbs } from '../../../components/shared';
import { routeConstants } from '../../../constants/routeConstants';
import { useGetMonthlyPostingByIdQuery } from '../../../services/GlPostings/GlPostingsService';
import { useGetProductCategoriesQuery } from '../../../services/Sublocation/SublocationService';
import MonthlyPostingDetailTopPanel from './components/MonthlyPostingDetailTopPanel';
import MonthlyPostingDetailTable from './components/MonthlyPostingDetailTable';
import SubLocationVariancePanel from './SubLocationVariancePanel';
import { buildVarianceTableData } from './helpers/monthlyReconciliationDataHelpers';
import useStyles from './style';
import { buildMonthlyPostingDetailViewModel } from './utils/monthlyPostingDetailHelpers';

const ERROR_MESSAGE = (id) =>
  `Unable to load Monthly Posting Details for ID ${id}. Please try again or contact support.`;
const FINALIZED_TAB_ROUTE = `${routeConstants.MONTHLY_RECONCILIATION_ROUTE}?selectedTab=2`;

const MonthlyReconciliationFinalizedDetail = () => {
  const classes = useStyles();
  const { id } = useParams();

  const { data: posting, isLoading, isError } = useGetMonthlyPostingByIdQuery(id, { skip: !id });
  const { data: productCategories = [] } = useGetProductCategoriesQuery({ isGLPostingEnabled: true });
  const detailViewModel = useMemo(
    () => buildMonthlyPostingDetailViewModel(posting, productCategories),
    [posting, productCategories],
  );
  const varianceTableData = useMemo(
    () => buildVarianceTableData(detailViewModel.assetsVariancesData || []),
    [detailViewModel.assetsVariancesData],
  );

  if (!id) {
    return (
      <Box>
        <Breadcrumbs
          variant="body2"
          underline="hover"
          data={[{ text: 'Monthly Reconciliation', redirection: FINALIZED_TAB_ROUTE }, { text: 'Finalized Detail' }]}
        />
        <Typography color="error">Missing posting ID.</Typography>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={280}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !posting) {
    return (
      <Box>
        <Breadcrumbs
          variant="body2"
          underline="hover"
          data={[{ text: 'Monthly Reconciliation', redirection: FINALIZED_TAB_ROUTE }, { text: 'Finalized Detail' }]}
        />
        <Alert severity="error" sx={{ mt: 2 }}>
          {ERROR_MESSAGE(id)}
        </Alert>
      </Box>
    );
  }

  if (!detailViewModel.hasPostingData) {
    return (
      <>
        <Box mb={3}>
          <Breadcrumbs
            variant="body2"
            underline="hover"
            data={[{ text: 'Monthly Reconciliation', redirection: FINALIZED_TAB_ROUTE }, { text: 'Finalized Detail' }]}
          />
        </Box>
        <Alert severity="warning">
          Posting data not returned by the API. Ensure GET /api/gl-postings/monthly/:id returns the posting.
        </Alert>
      </>
    );
  }

  return (
    <>
      <Box mb={3}>
        <Breadcrumbs
          variant="body2"
          underline="hover"
          data={[{ text: 'Monthly Reconciliation', redirection: FINALIZED_TAB_ROUTE }, { text: 'Finalized Detail' }]}
        />
      </Box>

      <Box mb={3}>
        <Typography variant="h1">{detailViewModel.postingHeader}</Typography>
      </Box>

      <MonthlyPostingDetailTopPanel
        x3ProductName={detailViewModel.x3ProductName}
        dateRange={detailViewModel.dateRange}
        postingDate={detailViewModel.postingDate}
        finalizedBy={detailViewModel.finalizedBy}
        finalizedTimestamp={detailViewModel.finalizedTimestamp}
      />

      {detailViewModel.isEmpty ? (
        <Paper elevation={0} className={classes.emptyStateContainer}>
          <Box className={classes.emptyStateBox}>
            <Typography variant="body2" color="text.secondary">
              No postings found for this reconciliation window.
            </Typography>
          </Box>
        </Paper>
      ) : (
        <>
          <MonthlyPostingDetailTable rows={detailViewModel.tableRows} />

          {detailViewModel.assetsVariancesData && detailViewModel.assetsVariancesData.length > 0 && (
            <Box mt={3}>
              <Typography variant="subtitle2" fontWeight={600} mb={2}>
                Asset-Level Variance Details ({detailViewModel.assetsVariancesData.length} items)
              </Typography>
              <SubLocationVariancePanel data={varianceTableData} />
            </Box>
          )}
        </>
      )}
    </>
  );
};

export default MonthlyReconciliationFinalizedDetail;
